import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useRouteParams } from "@vueuse/router";
import { useMetadataExperienceApi } from "@/composables/useMetadataExperienceApi";
import {
  useExperienceAdditionalServicesQuery,
  EXPERIENCE_ADDITIONAL_SERVICES_QUERY_KEY,
} from "@/features/experience-curation/queries/useExperienceAdditionalServicesQuery";
import { useCurationOwnOffer } from "@/features/experience-curation/sections/settings/own-offer/useCurationOwnOffer";
import { useCurationProductBrand } from "@/features/experience-curation/sections/settings/product-brand/useCurationProductBrand";
import { useCurationPromotional } from "@/features/experience-curation/sections/settings/promotional/useCurationPromotional";
import { useCurationNatGeo } from "@/features/experience-curation/sections/settings/nat-geo/useCurationNatGeo";
import { useCurationDuration } from "@/features/experience-curation/sections/content-generation/duration/useCurationDuration";
import { useCurationFeatures } from "@/features/experience-curation/sections/content-generation/features/useCurationFeatures";
import { useCurationAnimalWelfareTags } from "@/features/experience-curation/sections/distribution-tags/animal-welfare/useCurationAnimalWelfareTags";
import { useCurationHealthAndSafetyTags } from "@/features/experience-curation/sections/distribution-tags/health-and-safety/useCurationHealthAndSafetyTags";
import { useCurationOtherTags } from "@/features/experience-curation/sections/distribution-tags/other-tags/useCurationOtherTags";

/**
 * Single mutation responsible for persisting the entire `experience-additional-services`
 * resource. It reads from all 9 field stores that contribute codes to this endpoint.
 */
export function useCurationAdditionalServicesSaveMutation() {
  const experienceId = useRouteParams<string>("id");
  const queryClient = useQueryClient();
  const metadataService = useMetadataExperienceApi();

  const additionalServicesQuery = useExperienceAdditionalServicesQuery(experienceId);

  const ownOfferStore = useCurationOwnOffer();
  const productBrandStore = useCurationProductBrand();
  const promotionalStore = useCurationPromotional();
  const natGeoStore = useCurationNatGeo();
  const durationStore = useCurationDuration();
  const featuresStore = useCurationFeatures();
  const animalWelfareStore = useCurationAnimalWelfareTags();
  const healthAndSafetyStore = useCurationHealthAndSafetyTags();
  const otherTagsStore = useCurationOtherTags();

  function buildPayload(): string[] {
    return [
      ownOfferStore.model,
      productBrandStore.model,
      promotionalStore.model,
      natGeoStore.model,
      durationStore.model,
      ...featuresStore.model,
      ...animalWelfareStore.model,
      ...healthAndSafetyStore.model,
      ...otherTagsStore.model,
    ].filter((code): code is string => Boolean(code));
  }

  return useMutation({
    mutationFn: async () => {
      const existingId = additionalServicesQuery.data.value?.id;
      const payload = {
        experience_id: experienceId.value,
        additional_services: buildPayload(),
      };

      if (existingId) {
        await metadataService.updateAdditionalServices(existingId, payload);
      } else {
        await metadataService.createAdditionalServices(payload);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [EXPERIENCE_ADDITIONAL_SERVICES_QUERY_KEY] });
    },
  });
}
